/**
 * Access Control API client for GhostFlow per-section access control.
 * Handles fetching, configuring, and resetting per-user per-section access.
 *
 * Implements Binom/Keitaro-style granular access control with 9 sections × 5 levels.
 */

import { api } from './client'

// =============================================================================
// Types
// =============================================================================

/** Platform sections that access can be configured for. */
export type Section =
  | 'campaigns'
  | 'landings'
  | 'offers'
  | 'networks'
  | 'sources'
  | 'domains'
  | 'reports'
  | 'billing'
  | 'settings'

/** Access level for a section. */
export type AccessLevel =
  | 'full'
  | 'read_only'
  | 'selected'
  | 'created_and_selected'
  | 'none'

/** All available sections in display order. */
export const ALL_SECTIONS: Section[] = [
  'campaigns',
  'landings',
  'offers',
  'networks',
  'sources',
  'domains',
  'reports',
  'billing',
  'settings',
]

/** All available access levels. */
export const ALL_ACCESS_LEVELS: AccessLevel[] = [
  'full',
  'read_only',
  'selected',
  'created_and_selected',
  'none',
]

/** Human-readable labels for sections. */
export const SECTION_LABELS: Record<Section, string> = {
  campaigns: 'Campaigns',
  landings: 'Landing Pages',
  offers: 'Offers',
  networks: 'Affiliate Networks',
  sources: 'Traffic Sources',
  domains: 'Domains',
  reports: 'Reports',
  billing: 'Billing',
  settings: 'Settings',
}

/** Human-readable labels for access levels. */
export const ACCESS_LEVEL_LABELS: Record<AccessLevel, string> = {
  full: 'Full access',
  read_only: 'Read only',
  selected: 'Selected',
  created_and_selected: 'Created and selected',
  none: 'No access',
}

/** Human-readable descriptions for access levels. */
export const ACCESS_LEVEL_DESCRIPTIONS: Record<AccessLevel, string> = {
  full: 'Read, write, and delete all entities',
  read_only: 'View only, no modifications',
  selected: 'Only selected entities (by ID)',
  created_and_selected: 'Own entities + selected entities',
  none: 'Section hidden from user',
}

/** Report columns that can be hidden. */
export const HIDEABLE_COLUMNS = [
  'profit',
  'revenue',
  'cost',
  'roi',
  'epc',
  'epl',
  'cr',
  'ctr',
] as const

export type HideableColumn = (typeof HIDEABLE_COLUMNS)[number]

// =============================================================================
// API Response/Request interfaces
// =============================================================================

/** Single section access configuration (from API). */
export interface SectionAccessConfig {
  section: Section
  accessLevel: AccessLevel
  canCreate: boolean
  allowedIds: string[]
  hiddenColumns: string[]
}

/** Full user access response from API. */
export interface UserAccessResponse {
  teamId: string
  userId: string
  role: string
  sections: SectionAccessConfig[]
}

/** Entity picker item for "Selected" mode. */
export interface EntityPickerItem {
  id: string
  name: string
  status: string
}

/** Entity picker paginated response. */
export interface EntityPickerResponse {
  entities: EntityPickerItem[]
  total: number
  hasMore: boolean
}

/** Request to set user access (batch all sections). */
export interface SetUserAccessRequest {
  sections: Array<{
    section: Section
    accessLevel: string
    canCreate: boolean
    allowedIds?: string[]
    hiddenColumns?: string[]
  }>
}

// =============================================================================
// API Functions
// =============================================================================

export const accessApi = {
  /**
   * Get a user's access configuration for all sections.
   * Owner/Admin can view any user's access; any user can view their own.
   */
  getUserAccess: (teamId: string, userId: string) =>
    api.get<UserAccessResponse>(`/v1/teams/${teamId}/members/${userId}/access`),

  /**
   * Configure a user's access for all sections (batch update).
   * Required: Owner or Admin role.
   */
  setUserAccess: (teamId: string, userId: string, data: SetUserAccessRequest) =>
    api.put<UserAccessResponse>(`/v1/teams/${teamId}/members/${userId}/access`, data),

  /**
   * Reset a user's access to role defaults.
   * Required: Owner or Admin role.
   */
  resetUserAccess: (teamId: string, userId: string) =>
    api.delete<UserAccessResponse>(`/v1/teams/${teamId}/members/${userId}/access`),

  /**
   * Get the current user's effective access for the team in X-Team-ID header.
   * Any authenticated user can access their own effective access.
   */
  getEffectiveAccess: () =>
    api.get<UserAccessResponse>('/v1/me/effective-access'),

  /**
   * Get entities for the entity picker in "Selected" mode.
   * Used when configuring which specific entities a user can access.
   */
  getSectionEntities: (
    teamId: string,
    section: Section,
    params?: { search?: string; limit?: number; offset?: number }
  ) => {
    const searchParams = new URLSearchParams()
    if (params?.search) searchParams.set('search', params.search)
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.offset) searchParams.set('offset', params.offset.toString())
    const query = searchParams.toString()
    const url = `/v1/teams/${teamId}/access/${section}/entities${query ? `?${query}` : ''}`
    return api.get<EntityPickerResponse>(url)
  },
}
